import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, Flashlight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HttpService from '@/shared/services/http.service';
import { API_CONFIG } from '@/shared/constants/api';
import { logEvent, getDeviceModel } from '@/analytics';

// Extend MediaTrackConstraints to include torch property
declare global {
  interface MediaTrackConstraints {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
}

interface InboundScannerProps {
  onError: (error: string) => void;
  error?: string;
}

const InboundScanner = ({ onError, error }: InboundScannerProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentCamera, setCurrentCamera] = useState<'environment' | 'user'>('environment');
  const [torchOn, setTorchOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const playSuccessFeedback = () => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (error) {
      console.error(error);
    }

    try {
      navigator.vibrate?.(100);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCapture = async () => {
    if (!stream || !videoRef.current || !canvasRef.current) return;

    setIsUploading(true);
    setUploadProgress(0);
    playSuccessFeedback();

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const cameraLabel = stream.getVideoTracks()[0]?.label || 'unknown';
      logEvent('image_capture_resolution', {
        flow: 'inbound',
        width: canvas.width,
        height: canvas.height,
        resolution: `${canvas.width}x${canvas.height}`,
        model: getDeviceModel(),
        userAgent: navigator.userAgent.slice(0, 100),
        platform: navigator.platform,
        cameraLabel: cameraLabel.slice(0, 100),
      });

      canvas.toBlob(
        async (blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = (ev) => setCapturedImage(ev.target?.result as string);
            reader.readAsDataURL(blob);

            stream.getTracks().forEach((track) => track.stop());

            const uploadStartTime = performance.now();
            const slowUploadTimer = setTimeout(() => {
              logEvent('manual_entry_fallback', {
                reason: 'scan_server_timeout',
              });
            }, 5000);

            try {
              setUploadProgress(25);
              const presignedResponse = await HttpService.post(API_CONFIG.path.presignedUrl, {
                fileType: 'image/jpeg',
                extension: 'jpg',
              });

              setUploadProgress(50);
              const uploadUrl = presignedResponse.uploadUrl;
              setUploadProgress(75);

              const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: {
                  'Content-Type': 'image/jpeg',
                },
              });
              clearTimeout(slowUploadTimer);
              const uploadDurationSec = Number(((performance.now() - uploadStartTime) / 1000).toFixed(2));
              logEvent('image_upload_duration', {
                flow: 'inbound',
                durationSec: uploadDurationSec,
                duration_bucket: uploadDurationSec.toFixed(2),
                success: uploadResponse.ok ? 'true' : 'false',
              });

              if (uploadResponse.ok) {
                setUploadProgress(100);
                const imageUrl = uploadUrl.split('?')[0];
                navigate(`/inbound?photo=true&imageUrl=${encodeURIComponent(imageUrl)}`);
              } else {
                onError(t('upload_failed_retry'));
              }
            } catch (error) {
              clearTimeout(slowUploadTimer);
              const uploadDurationSec = Number(((performance.now() - uploadStartTime) / 1000).toFixed(2));
              logEvent('image_upload_duration', {
                flow: 'inbound',
                durationSec: uploadDurationSec,
                duration_bucket: uploadDurationSec.toFixed(2),
                success: 'false',
              });
              console.error('Error uploading photo:', error);
              onError(t('upload_failed_retry'));
            } finally {
              setIsUploading(false);
            }
          }
        },
        'image/jpeg',
        0.95,
      );
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    startCamera(currentCamera);
  };

  const switchCamera = async () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    const newCamera = currentCamera === 'environment' ? 'user' : 'environment';
    setCurrentCamera(newCamera);
    startCamera(newCamera);
  };

  const toggleTorch = async () => {
    try {
      if (stream) {
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities() as MediaTrackCapabilities;
        if (capabilities.torch) {
          await track.applyConstraints({
            advanced: [{ torch: !torchOn }],
          });
          setTorchOn(!torchOn);
        }
      }
    } catch (error) {
      console.error('Torch not available:', error);
    }
  };

  const startCamera = useCallback(
    async (camera: 'environment' | 'user' = 'environment') => {
      try {
        setCameraError(null);
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: camera,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
          },
          audio: false,
        });

        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }

        const track = mediaStream.getVideoTracks()[0];
        const settings = track.getSettings();
        if ((settings.width && settings.width < 1920) || (settings.height && settings.height < 1080)) {
          logEvent('camera_resolution_below_requested', {
            flow: 'inbound',
            requestedWidth: 1920,
            requestedHeight: 1080,
            actualWidth: settings.width ?? 0,
            actualHeight: settings.height ?? 0,
          });
        }
      } catch (err) {
        const errorMessage = 'Camera access denied. Please allow camera permissions.';
        setCameraError(errorMessage);
        onError(errorMessage);
      }
    },
    [onError],
  );

  useEffect(() => {
    // Start camera immediately when component mounts since we're only using step 2
    startCamera();
  }, [startCamera]);

  // Step 2: Image Capture
  return (
    <div className="relative w-full h-[calc(100vh-97px)] overflow-hidden rounded-lg flex flex-col">
      {/* Instructions banner - outside camera area */}
      <div className="relative z-20 pointer-events-none mb-4">
        <p className="text-foreground/90 text-sm font-medium mb-1">{t('capture_the_complete_parcel')}</p>
        <p className="text-foreground/70 text-xs">{t('ensure_all_text_and')}</p>
      </div>

      {/* Camera area - below instructions */}
      <div className="relative h-[calc(100%-56px)] overflow-hidden rounded-lg">
        {/* Camera error display */}
        {cameraError ? (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
            <div className="text-center p-6 max-w-sm">
              {/* Icon */}
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-red-600" />
              </div>
              {/* Instructions */}
              <div className="text-left text-sm text-gray-600 mb-5 space-y-1">
                <p className="text-red-600 font-semibold text-lg mb-2">{t('camera_is_blocked_by')}</p>
                {/* Error Message */}
                <p className="text-red-600 text-sm mb-4 text-center">{cameraError || 'Permission denied'}</p>
                <p className="mt-2 font-medium">{t('how_to_fix')}</p>
                <p>{t('1_click_icon_in')}</p>
                <p>
                  {t('2_set')} <b>{t('camera_allow')}</b>
                </p>
                <p>{t('3_reload_this_page')}</p>
                <p className="mt-2 text-xs text-gray-500">{t('if_it_still_doesnt')}</p>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                {/* Retry Camera (PRIMARY ACTION) */}
                <Button onClick={() => startCamera(currentCamera)} className="w-full">
                  {t('retry_camera_access')}
                </Button>
                {/* Reload (SECONDARY ACTION) */}
                <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                  {t('reload_page')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* State 1: Camera capture */}
            {!capturedImage && (
              <>
                <video
                  ref={videoRef}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  autoPlay
                  playsInline
                />
                <div className="absolute top-20 right-1/2 translate-x-1/2 flex gap-2 pointer-events-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/70 hover:bg-gray-700/50 bg-black/20 backdrop-blur-sm"
                    onClick={toggleTorch}
                  >
                    <Flashlight className={`w-6 h-6 ${torchOn ? 'text-yellow-400' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/70 hover:bg-gray-700/50 bg-black/20 backdrop-blur-sm"
                    onClick={switchCamera}
                  >
                    <RotateCcw className="w-6 h-6" />
                  </Button>
                </div>
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 pointer-events-auto flex flex-col items-center w-[80%]">
                  <Button
                    className="w-16 h-16 mx-auto mb-2 rounded-full bg-blue-500 flex items-center justify-center"
                    onClick={handleCapture}
                  >
                    <Camera className="!w-8 !h-8 text-white" />
                  </Button>
                  <p className="text-white/80 text-sm text-center">{t('place_entire_invoice_on')}</p>
                </div>
              </>
            )}
            {capturedImage && (isUploading || error) && (
              <>
                {/* Shared Background Image */}
                <img
                  src={capturedImage}
                  alt={t('captured_invoice')}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                />

                {/* Shared Overlay */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white rounded-xl p-6 max-w-sm flex flex-col items-center gap-3 w-full m-4">
                    {/* Uploading State */}
                    {isUploading && (
                      <>
                        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-gray-900 font-medium">{t('uploading_image')}</p>

                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>

                        <p className="text-gray-600 text-sm">
                          {uploadProgress}
                          {t('complete')}
                        </p>
                      </>
                    )}

                    {/* Error State */}
                    {!isUploading && error && (
                      <>
                        <p className="text-red-600 font-medium text-center">
                          {error || 'Upload failed. Please try again!'}
                        </p>
                        <Button onClick={handleRetake} className="w-full">
                          {t('retake_photo')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default InboundScanner;
